import React from 'react';
import { Autocomplete, Button, Card, Chip, Fab, FormGroup, Grid, Hidden, IconButton, ListItemIcon, Menu, MenuItem, Modal, Stack, TextField, Tooltip, Typography } from '@mui/material';
import config from 'src/config';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import showLoading from 'src/common/loading';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import LabelIcon from '@mui/icons-material/Label';
import constants from '../constants/common';
import { addContainer, getContainers } from 'src/services/container.service';
import { suggestLabels } from 'src/services/label.service';
import { generateLabels, getFile, updateFile } from 'src/services/file.service';
import ContainersAutocomplete from '../autocomplete/containers';
import FileAuditLogModal from '../file-audit-log/FileAuditLog.modal';
import { MoreVert, Redo, Undo } from "@mui/icons-material";
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AddIcon from '@mui/icons-material/Add';
import FileWorkflows from '../files/FileWorkflows';
import AddEditNoteModal from './AddEditNote.modal';

// ----------------------------------------------------------------------

const flags = constants.flags;


class NoteInfoModal extends React.Component {

	constructor(props) {
		super(props);

		this.state = {

			role: localStorage.getItem('role') || config.USER_ROLES.USER,
			mode: this.props.file ? 'edit' : 'add',
			add_view_type: 'fab',
			is_modal_open: this.props.open || false,

			file: null,
			is_owner: false,

			name: '',
			suggested_name: '',
			ai_suggested_name: '',
			flags: [],
			labels: [],
			label_suggestions: [],

			containers: [],
			container_suggestions: [],
			shared_users: [],

			generating_labels: false,
		}
	}

	// promised setState
	setStateAsync(state) {
		return new Promise((resolve) => {
			this.setState(state, resolve)
		});
	}

	async componentDidMount() {
		if (this.props.file) {
			this.fetchFile();
		}
	}

	async componentDidUpdate(prevProps) {
		if (this.props.file && this.props.file !== prevProps.file) {
			this.fetchFile();
		}
	}

	async fetchFile() {

		let file = await getFile(this.props.file._id);
		if (!file) {
			return;
		}
		if (file.error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: file.error,
				confirmButtonText: 'Ok',
				confirmButtonColor: config.primaryColor
			});
			return;
		}

		let logged_in_username = localStorage.getItem('username');
		file = file.file;

		let shared_users = file?.shared_users || [];
		if (shared_users.length > 0) {
			shared_users = shared_users.map(user => user.username);
		}

		await this.setState({
			file: file,
			is_owner: file?.owner?.username === logged_in_username,
			name: file?.name ?? '',
			suggested_name: file?.suggested_name ? file.suggested_name : file.name,
			ai_suggested_name: file?.ai_suggested_name ? file.ai_suggested_name : file.name,
			flags: file?.flags ? file.flags : [],
			labels: file?.labels ? file.labels : [],
			shared_users: shared_users,
			containers: file?.containers ? file.containers.map(container => {
				return {
					id: container._id,
					label: container.name,
					type: container.type,
					color: container.color,
					is_owner: container.is_owner
				}
			}) : [],
			mode: 'edit',
			is_modal_open: true
		});

	}

	isFormValid() {
		if (!this.state.name || this.state.name.trim() === '') {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: 'Name is required'
			});
			return false;
		}
		return true;
	}

	clearForm() {
		this.setState({
			file: null,
			name: '',
			flags: [],
			labels: [],
			containers: [],
			label_suggestions: [],
			container_suggestions: []
		});
	}

	async editNote() {
		if (!this.isFormValid())
			return;

		showLoading(true);
		let editNoteRes = await updateFile(this.props.file._id, {
			name: this.state.name,
			suggested_name: this.state.suggested_name,
			flags: this.state.flags,
			labels: this.state.labels,
			containers: this.state.containers.map(container => container.id),
			shared_users: this.state.shared_users,
			organized: true
		});
		if (editNoteRes.error) {
			Swal.fire({
				icon: 'error',
				title: 'Error',
				text: editNoteRes.error
			});
		} else {
			Swal.fire({
				icon: 'success',
				title: 'Success',
				text: 'Note organized successfully'
			});
			this.setState({
				is_modal_open: false,
			});
			this.clearForm();
			if (this.props.onClose)
				this.props.onClose();
		}
	}

	async organizeNote() {
		await this.editNote();

		if (this?.props?.onReload) {
			await this.props.onReload();
		}
	}

	generateLabels = async () => {
		try {

			await this.setStateAsync({
				generating_labels: true
			});

			let labels_response = await generateLabels(this.props.file._id);
			if (labels_response.error) {
				console.log("Error generating labels:", labels_response.error);
				alert("Error generating labels - " + labels_response.error)
				await this.setStateAsync({
					generating_labels: false
				});
				return;
			}

			let generated_labels = labels_response.labels;
			let labels = this.state.labels;
			labels = labels.concat(generated_labels);

			// unique labels
			labels = [...new Set(labels)];

			await this.setStateAsync({
				generating_labels: false,
				labels: labels
			});
		} catch (error) {
			await this.setStateAsync({
				generating_labels: false
			});
			alert("Unknown error occurred while generating labels - " + error.message)
			console.log("Unknown error occurred while generating labels - ", error.message)
		}
	}

	toggleLabelsVisibility = () => {
		this.setState({
			is_labels_visible: !this.state.is_labels_visible
		});
	}

	updateLabelSuggestions = async (e) => {

		let query = e.target.value;

		let label_suggestions = await suggestLabels({ query });

		this.setState({
			label_suggestions: label_suggestions?.next_labels ? label_suggestions.next_labels : []
		});
	}

	updateContainerSuggestions = async (params = null) => {
		try {

			let query = {
				limit: 10,
				skip: 0
			};

			let search = '';
			if (params) {
				search = params.target.value;
				query.search = search;
			}

			let containers = await getContainers(query);

			if (containers.error) {
				console.log("Error fetching containers:", containers.error);
				this.setState({
					container_suggestions: []
				});
				return;
			}

			containers = containers.containers;

			let container_suggestions = containers.map(container => {
				return {
					id: container._id,
					label: container.name,
					type: container.type,
					color: container.color,
					is_owner: container.is_owner
				}
			});

			if (search && search.trim() !== '') {
				container_suggestions.push({
					id: search,
					label: `Create "${search}"`,
					is_owner: true,
					create: true
				});
			}


			this.setState({
				container_suggestions: container_suggestions
			});

		} catch (error) {
			console.error("Error fetching container suggestions:", error);
		}
	}

	createContainer = async (container_name) => {
		let createdContainer = await addContainer({
			name: container_name
		})
		if (createdContainer.error) {
			return null;
		}
		return createdContainer.container;
	}

	viewNote = async (file) => {
        if (!file) {
            return;
        }
		this.setState({ file: file, viewNoteModal: true });
	}

	render() {
		return (
			<>
				{(!this.props.hideAddButton) ? (
					<Fab size="small" color="secondary" aria-label="add"
						onClick={() => {
							this.setState({
								is_modal_open: true,
								mode: 'add',
								file: null,
								name: '',
							});
						}}
					>
						<StickyNote2Icon />
					</Fab>
				) : null}
				<Modal
					open={this.state.is_modal_open}
					onClose={() => {
						this.setState({
							is_modal_open: false,
							file: null,
							name: '',
						});
						if (this.props.onClose)
							this.props.onClose();
					}}
				>
					<Card sx={modalStyle}>
						<Grid container spacing={2} sx={{ p: 2, pb: 0 }} justifyContent={'center'} alignItems={'center'}>
							<Grid item xs={12}>
								<Stack spacing={1} direction={'row'}>

									<Grid display={'flex'} flex={1} flexDirection={'row'} alignItems="center">
										<Typography
											variant="h6"
											color="textSecondary"
											contentEditable={this.state.is_owner ? true : false}
											suppressContentEditableWarning={true}
											spellCheck={false}
											onBlur={(e) => {
												this.setState({
													isTitleFocussed: false,
													suggested_name: e.currentTarget.innerText
												});
											}}
											onFocus={() => {
												this.setState({
													isTitleFocussed: true
												});
											}}
											style={{
												alignItems: 'center',
												...(this.state.isTitleFocussed ? {} : {
													WebkitLineClamp: 2,
													WebkitBoxOrient: 'vertical',
													overflow: 'hidden',
													display: '-webkit-box',
													textOverflow: 'ellipsis',
													maxHeight: '2.8em'
												}),
												display: '-webkit-box',
												marginRight: '16px'
											}}
											sx={{
												fontSize: { xs: '0.725rem', md: '1rem' },
												textAlign: 'justify'
											}}
										>
											{this.state.suggested_name}
										</Typography>
										<Hidden mdDown>
											<Grid display={'flex'} flexDirection={'row'} alignItems="center">
												<Chip
													label="Open"
													size="small"
													variant="filled"
													onClick={() => {
														this.viewNote(this.state.file);
													}}
													sx={{
														px: 2,
														py: 1,
														backgroundColor: 'black',
														color: 'white',
														'&:hover': {
															backgroundColor: '#333',
															cursor: 'pointer'
														}
													}}
												/>
											</Grid>
										</Hidden>
									</Grid>


									<Grid display={'flex'} flexDirection={'row'} alignItems="center">
										<Hidden mdDown>
											{(this.state.name !== this.state.suggested_name) && (
												<Grid display={'flex'} flexDirection={'row'} alignItems="center">
													<Tooltip title={"Revert to original name"} arrow>
														<IconButton
															onClick={() => {
																this.setState({
																	suggested_name_copy: this.state.suggested_name,
																	suggested_name: this.state.name
																});
															}}
														>
															<Undo fontSize="small" style={{ color: 'gray', opacity: 0.6 }} />
														</IconButton>
													</Tooltip>
												</Grid>
											)}
											{(this.state.name == this.state.suggested_name && this.state.suggested_name_copy) && (
												<Grid display={'flex'} flexDirection={'row'} alignItems="center">
													<Tooltip title={"Revert to previous name"} arrow>
														<IconButton
															onClick={() => {
																this.setState({
																	suggested_name: this.state.suggested_name_copy,
																	suggested_name_copy: null
																});
															}}
														>
															<Redo fontSize="small" style={{ color: 'gray', opacity: 0.6 }} />
														</IconButton>
													</Tooltip>
												</Grid>
											)}
											<FormGroup sx={{ flexDirection: 'row', alignItems: 'center' }} >
												{flags.map((flag, index) => {
													return (
														<Tooltip key={index} title={flag.label} arrow>
															<IconButton
																sx={{
																	border: this.state.flags.includes(flag.value) ? `1px solid ${flag.color}` : '',
																	marginRight: '5px',
																}}
																onClick={() => {
																	if (this.state.flags.includes(flag.value)) {
																		this.setState({
																			flags: this.state.flags.filter(f => f !== flag.value)
																		});
																	} else {
																		this.setState({
																			flags: [...this.state.flags, flag.value]
																		});
																	}
																}}
															>
																{this.state.flags.includes(flag.value) ? flag.iconSelected : flag.icon}
															</IconButton>
														</Tooltip>
													)
												})}
											</FormGroup>
										</Hidden>
										<Hidden mdUp>
											<IconButton
												onClick={(event) => {
													this.setState({ anchorEl: event.currentTarget });
												}}
											>
												<MoreVert fontSize="small" style={{ color: 'gray', opacity: 0.6 }} />
											</IconButton>
											<Menu
												anchorEl={this.state.anchorEl}
												open={Boolean(this.state.anchorEl)}
												onClose={() => this.setState({ anchorEl: null })}
											>
												{(this.state.name !== this.state.suggested_name) && (
													<MenuItem
														onClick={() => {
															this.setState({
																suggested_name_copy: this.state.suggested_name,
																suggested_name: this.state.name
															});
														}}
													>
														<ListItemIcon>
															<Undo fontSize="small" />
														</ListItemIcon>
														Undo
													</MenuItem>
												)}
												{(this.state.name == this.state.suggested_name && this.state.suggested_name_copy) && (
													<MenuItem
														onClick={() => {
															this.setState({
																suggested_name: this.state.suggested_name_copy,
																suggested_name_copy: null
															});
														}}
													>
														<ListItemIcon>
															<Redo fontSize="small" />
														</ListItemIcon>
														Redo
													</MenuItem>
												)}
												{flags.map((flag, index) => {
													return (
														<MenuItem
															onClick={() => {
																if (this.state.flags.includes(flag.value)) {
																	this.setState({
																		flags: this.state.flags.filter(f => f !== flag.value)
																	});
																} else {
																	this.setState({
																		flags: [...this.state.flags, flag.value]
																	});
																}
															}}
														>
															<ListItemIcon>
																{this.state.flags.includes(flag.value) ? flag.iconSelected : flag.icon}
															</ListItemIcon>
															{flag.label}
														</MenuItem>
													)
												})}
											</Menu>
										</Hidden>
									</Grid>

								</Stack>
							</Grid>

							<Hidden mdUp>
								<Grid item xs={12}>
									<Chip
										label="Open"
										size="small"
										variant="filled"
										onClick={() => {
											this.viewNote(this.state.file);
										}}
										sx={{
											px: 2,
											py: 1,
											backgroundColor: 'black',
											color: 'white',
											'&:hover': {
												backgroundColor: '#333',
												cursor: 'pointer'
											}
										}}
									/>
								</Grid>
							</Hidden>

							{(this.state?.file?.what_is_it) && (
								<Grid item xs={12}>
									<Typography
										variant="body1"
										color="textSecondary"
										fontSize="0.7rem"
										sx={{
											fontSize: { xs: '0.7rem', md: '1rem' },
											textAlign: 'justify'
										}}
									>
										{this.state.file.what_is_it}
									</Typography>
								</Grid>
							)}

							<Grid item xs={12}>
								<FileWorkflows
									file={this.state.file}
								/>
							</Grid>

							<Grid item xs={12}>
								<Stack spacing={1} direction={'row'}>
									<ContainersAutocomplete
										label={"Part of"}
										disabled={!this.state.is_owner}
										containers={this.state.containers}
										onChange={(updatedContainers) => {
											this.setState({
												containers: updatedContainers
											});
										}}
									/>
								</Stack>
								<Stack spacing={1} direction={'row'} sx={{ marginTop: '10px' }}>
									<Chip
										icon={<AddIcon />}
										label="Labels"
										size="small"
										variant={this.state.is_labels_visible ? "filled" : "outlined"}
										onClick={this.toggleLabelsVisibility}
										sx={{ border: '0px' }}
									/>
								</Stack>

								{(this.state.is_labels_visible) ? (
									<Stack spacing={1} direction={'row'} mt={1}>
										<Autocomplete
											freeSolo
											multiple
											id="labels-outlined"
											options={this.state.label_suggestions}
											value={this.state.labels}
											onChange={(event, newValue) => {
												this.setState({
													labels: newValue
												});
											}}
											onFocus={() => this.setState({ labelsFocused: true })}
											onBlur={() => this.setState({ labelsFocused: false })}
											renderTags={(tagValue, getTagProps) => {
												if (this.state.labelsFocused) {
													// Show all chips while focused
													return tagValue.map((option, index) => (
														<Chip
															{...getTagProps({ index })}
															size="small"
															key={option}
															label={option}
															variant="outlined"
														/>
													));
												}

												// Show only first 4 chips when blurred
												const visible = tagValue.slice(0, 4);
												const remaining = tagValue.length - visible.length;

												return (
													<>
														{visible.map((option, index) => (
															<Chip
																{...getTagProps({ index })}
																size="small"
																key={option}
																label={option}
																variant="outlined"
															/>
														))}

														{remaining > 0 && (
															<Chip
																size="small"
																label={`+${remaining} more`}
																variant="outlined"
																sx={{ ml: 0.5 }}
															/>
														)}
													</>
												);
											}}
											renderInput={(params) => (
												<TextField
													{...params}
													variant="outlined"
													label="Labels"
													placeholder="What is it ?"
													InputProps={{
														...params.InputProps,
														endAdornment: (
															<React.Fragment>
																{params.InputProps.endAdornment}
																<LabelIcon />
															</React.Fragment>
														),
														enterKeyHint: 'enter'
													}}
													onChange={this.updateLabelSuggestions}
												/>
											)}
											fullWidth
											size="small"
											enterKeyHint='enter'
										/>
									</Stack>
								) : null}

							</Grid>

							<Grid item xs={12}>
								<Button
									disableElevation
									fullWidth
									variant="contained"
									onClick={async () => {
										this.organizeNote();
									}}
									disabled={this.state.loading}
									size="small"
								>
									{this.state.loading ? 'Organizing...' : 'Organize'}
								</Button>
							</Grid>

							<Grid item xs={12} direction={'row'} style={{ paddingTop: 0 }}>
								<Stack spacing={1} direction={'row'} mb={1} justifyContent={"space-between"} alignItems={"center"}>

									<Stack spacing={1} direction={'row'} alignItems={"center"}>

										{/* Owners */}
										{((this.state.file?.owner) || (this.state.file?.co_owners && this.state.file?.co_owners.length > 0) || (this.state.file?.owner_emails && this.state.file?.owner_emails.length > 0)) ? (
											<Stack spacing={0} direction={'row'} alignItems={'center'}>
												{/* Icon */}
												<ShieldOutlinedIcon fontSize="small" style={{ color: 'gray', opacity: 0.6 }} />
												{/* owner */}
												{(this.state.file?.owner) ? (
													<Stack spacing={0} direction={'row'} alignItems={'center'}>
														<Chip
															size="small"
															label={this.state.file?.owner?.name ? this.state.file?.owner?.name : this.state.file?.owner?.username}
															variant="outlined"
															sx={{ m: 1, marginLeft: 0, borderRadius: '5px', opacity: 0.6, border: 'none' }}
														/>
													</Stack>
												) : null}
												{/* co_owners list */}
												{(this.state.file?.co_owners && this.state.file?.co_owners.length > 0) ? (
													// Chip fields
													<Stack spacing={0} direction={'row'} alignItems={'center'}>
														{this.state.file?.co_owners.map((co_owner, index) => {
															return (
																<Chip
																	size="small"
																	key={index}
																	label={co_owner?.name ? (co_owner?.name + ' (' + co_owner?.username + ')') : co_owner?.username}
																	variant="outlined"
																	sx={{ m: 1, borderRadius: '5px', opacity: 0.6, border: 'none' }}
																/>
															)
														})}
													</Stack>
												) : null}
												{(this.state.file?.owner_emails && this.state.file?.owner_emails.length > 0) ? (
													// Chip fields
													<Stack spacing={0} direction={'row'} alignItems={'center'}>
														{this.state.file?.owner_emails.map((owner_email, index) => {
															return (
																<Chip
																	size="small"
																	key={index}
																	label={owner_email}
																	variant="outlined"
																	sx={{ m: 1, borderRadius: '5px', opacity: 0.6, border: 'none' }}
																/>
															)
														})}
													</Stack>
												) : null}
											</Stack>
										) : null}

										{/* Shared Users */}
										<Hidden mdDown>
											{((this.state.file?.shared_users && this.state.file?.shared_users.length > 0) || (this.state.file?.shared_user_emails && this.state.file?.shared_user_emails.length > 0)) ? (
												<Stack spacing={0} direction={'row'} alignItems={'center'}>
													{/* Icon */}
													<PeopleAltIcon fontSize="small" style={{ color: 'gray', opacity: 0.6 }} />
													{/* Owners list */}
													{(this.state.file?.shared_users && this.state.file?.shared_users.length > 0) ? (
														// Chip fields
														<Stack spacing={0} direction={'row'} alignItems={'center'}>
															{this.state.file?.shared_users.map((shared_user, index) => {
																return (
																	<Chip
																		size="small"
																		key={index}
																		label={shared_user?.name ? shared_user?.name : shared_user?.username}
																		variant="outlined"
																		sx={{ m: 1, borderRadius: '5px', opacity: 0.6, border: 'none' }}
																	/>
																)
															})}
														</Stack>
													) : null}
													{(this.state.file?.shared_user_emails && this.state.file?.shared_user_emails.length > 0) ? (
														// Chip fields
														<Stack spacing={0} direction={'row'} alignItems={'center'}>
															{this.state.file?.shared_user_emails.map((shared_user_email, index) => {
																return (
																	<Chip
																		size="small"
																		key={index}
																		label={shared_user_email}
																		variant="outlined"
																		sx={{ m: 1, borderRadius: '5px', opacity: 0.6, border: 'none' }}
																	/>
																)
															})}
														</Stack>
													) : null}
												</Stack>
											) : null}
										</Hidden>

									</Stack>

									<Hidden mdDown>
										{/* Created and Modified At */}
										<Stack spacing={0} direction={'row'} alignItems={'center'}>
											{(this.state.file?.real_created_at) ? (
												<Chip
													size="small"
													label={"Created : " + moment(this.state.file?.real_created_at).format('LLL')}
													variant="outlined"
													sx={{ m: 1, borderRadius: '5px', opacity: 0.3, border: 'none' }}
												/>
											) : null}
											{(this.state.file?.real_modified_at) ? (
												<Chip
													size="small"
													label={"Modified : " + moment(this.state.file?.real_modified_at).format('LLL')}
													variant="outlined"
													sx={{ m: 1, borderRadius: '5px', opacity: 0.3, border: 'none' }}
												/>
											) : null}
										</Stack>

										{/* Original File Name */}
										{(this.state.file?.name && this.state.file.suggested_name !== this.state.file?.name) ? (
											<Stack spacing={0} direction={'row'} alignItems={'center'}>
												<Chip
													size="small"
													label={"Original Name : " + this.state.file?.name}
													variant="outlined"
													sx={{ m: 1, borderRadius: '5px', opacity: 0.2, border: 'none' }}
												/>
											</Stack>
										) : null}
									</Hidden>

									<FileAuditLogModal
										view_type='icon'
										file={this.state.file}
									/>

								</Stack>
							</Grid>
						</Grid>
					</Card>
				</Modal>
				{(this.state.viewNoteModal) ? (
					<AddEditNoteModal
						hideAddButton={true}
						file={this.state.file}
						open={this.state.viewNoteModal}
						onClose={() => this.setState({viewNoteModal: false})}
						onReload={this?.props?.onReload}
					/>
				) : null}
			</>
		);
	}
}

const modalStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: '80%',
	bgcolor: 'background.paper',
	boxShadow: 24,
	maxHeight: '90%',
	overflowY: 'auto'
};

export default function (props) {
	const navigate = useNavigate();

	return <NoteInfoModal {...props} navigate={navigate} />;
}
